import { useState, type FormEvent } from 'react';
import { Button, Field, Input, Select, Textarea } from '../ui';

export interface PurchaseFormValues {
  purchasedOn: string;
  description: string;
  defaultCondition: 'N' | 'U';
  totalCost: string;
  sellerName: string | null;
  sellerContact: string | null;
  vatScheme: 'none' | 'standard' | 'margin';
  allocationMethod: 'by_value' | 'equal';
  notes: string | null;
}

export const emptyPurchase = (): PurchaseFormValues => ({
  purchasedOn: new Date().toISOString().slice(0, 10),
  description: '',
  defaultCondition: 'U',
  totalCost: '',
  sellerName: null,
  sellerContact: null,
  vatScheme: 'none',
  allocationMethod: 'by_value',
  notes: null,
});

export function PurchaseForm({
  initial,
  submitLabel,
  busy,
  onSubmit,
}: {
  initial?: PurchaseFormValues;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (v: PurchaseFormValues) => void;
}) {
  const [v, setV] = useState<PurchaseFormValues>(initial ?? emptyPurchase());
  const set = <K extends keyof PurchaseFormValues>(k: K, value: PurchaseFormValues[K]) => setV((x) => ({ ...x, [k]: value }));

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ ...v, sellerName: v.sellerName?.trim() || null, sellerContact: v.sellerContact?.trim() || null, notes: v.notes?.trim() || null });
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Field label="Date bought">
        <Input type="date" required value={v.purchasedOn} onChange={(e) => set('purchasedOn', e.target.value)} />
      </Field>
      <Field label="Description" className="lg:col-span-2" hint="An order number or a reminder of what it was.">
        <Input value={v.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. eBay lot 1234, Ninjago bulk" />
      </Field>
      <Field label="Price paid" hint="Everything it cost you, postage included.">
        <Input inputMode="decimal" required placeholder="25.00" value={v.totalCost} onChange={(e) => set('totalCost', e.target.value)} />
      </Field>
      <Field label="Condition" hint="Stock added to this purchase starts with this condition.">
        <Select value={v.defaultCondition} onChange={(e) => set('defaultCondition', e.target.value as 'N' | 'U')}>
          <option value="U">Used</option>
          <option value="N">New</option>
        </Select>
      </Field>
      <Field label="Spread the price" hint="By value keeps a cheap part from costing as much as a minifigure.">
        <Select value={v.allocationMethod} onChange={(e) => set('allocationMethod', e.target.value as 'by_value' | 'equal')}>
          <option value="by_value">By value (recommended)</option>
          <option value="equal">Equally per piece</option>
        </Select>
      </Field>
      <Field label="VAT">
        <Select value={v.vatScheme} onChange={(e) => set('vatScheme', e.target.value as PurchaseFormValues['vatScheme'])}>
          <option value="none">Not VAT-registered</option>
          <option value="standard">Standard rate</option>
          <option value="margin">Second-hand margin scheme</option>
        </Select>
      </Field>
      <Field label="Seller">
        <Input value={v.sellerName ?? ''} onChange={(e) => set('sellerName', e.target.value)} />
      </Field>
      <Field label="Seller contact" className="lg:col-span-2">
        <Input value={v.sellerContact ?? ''} onChange={(e) => set('sellerContact', e.target.value)} />
      </Field>
      <Field label="Notes" className="sm:col-span-2 lg:col-span-4">
        <Textarea rows={2} value={v.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
      </Field>
      <div className="sm:col-span-2 lg:col-span-4">
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
